import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  ContactLanguageErrorCode,
  ContactLanguageState,
  SupportedLanguage,
} from "./contracts";
import { ContactLanguageRequestError } from "./contracts";

/**
 * The per-contact language preference lives in the WhatsApp bot's database,
 * not behind the dashboard API, so it is reached through its own Vercel proxy
 * (src/server/contactLanguageProxy.ts) — the same shape as the test-echo
 * client. The proxy injects the bot's internal key and derives the operator
 * from the Supabase session; the browser only ever sends its own token.
 */
const CONTACT_LANGUAGE_PROXY_PATH = "/api/contact-language";

export function fetchContactLanguage(whatsappId: string) {
  return request(whatsappId, { method: "GET" });
}

export function setContactLanguage(
  whatsappId: string,
  preferredLanguage: string | null,
  expectedUpdatedAt: string | null,
) {
  return request(whatsappId, {
    method: "PUT",
    body: JSON.stringify({
      preferred_language: preferredLanguage,
      expected_updated_at: expectedUpdatedAt,
    }),
  });
}

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function request(
  whatsappId: string,
  init: RequestInit,
): Promise<ContactLanguageState> {
  const response = await fetch(
    `${CONTACT_LANGUAGE_PROXY_PATH}/${encodeURIComponent(whatsappId)}`,
    {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(await authHeaders()),
      },
    },
  );
  const payload = (await response.json().catch(() => ({}))) as Record<
    string,
    unknown
  >;
  if (!response.ok) {
    throw contactLanguageErrorFromResponse(response.status, payload);
  }
  return parseContactLanguageState(payload);
}

/**
 * Map a proxy / bot refusal onto a typed error. Pure so it is unit-testable.
 * The bot's own error codes ride along as `message` (`payload.error`) and in
 * `details`, the way the runtime-config client does it.
 */
export function contactLanguageErrorFromResponse(
  status: number,
  payload: Record<string, unknown>,
): ContactLanguageRequestError {
  const upstreamCode =
    typeof payload.error === "string" && payload.error
      ? payload.error
      : `http_${status}`;
  const code: ContactLanguageErrorCode =
    status === 400
      ? "unsupported_language"
      : status === 401
        ? "unauthorized"
        : status === 403
          ? "forbidden"
          : status === 404
            ? "unknown_contact"
            : status === 409
              ? "conflict"
              : upstreamCode === "contact_language_unconfigured"
                ? "unconfigured"
                : status >= 500
                  ? "dependency_unavailable"
                  : "unknown";
  return new ContactLanguageRequestError(
    status,
    code,
    upstreamCode,
    payload.details &&
    typeof payload.details === "object" &&
    !Array.isArray(payload.details)
      ? (payload.details as Record<string, unknown>)
      : {},
  );
}

/**
 * A 200 that is not the contract (an HTML index page, an empty body) is an
 * error, not a preference — the selector must never render a picker off it.
 */
export function parseContactLanguageState(
  payload: Record<string, unknown>,
): ContactLanguageState {
  if (!Array.isArray(payload.supported_languages)) {
    throw new ContactLanguageRequestError(
      200,
      "unknown",
      "contact_language_malformed_response",
    );
  }
  const supported = payload.supported_languages
    .map(parseSupportedLanguage)
    .filter((language): language is SupportedLanguage => language !== null);
  return {
    whatsapp_user_id: String(payload.whatsapp_user_id ?? ""),
    preferred_language: nullableString(payload.preferred_language),
    source: nullableString(payload.source),
    updated_at: nullableString(payload.updated_at),
    supported_languages: supported,
    policy_mode: String(payload.policy_mode ?? "off"),
  };
}

function parseSupportedLanguage(value: unknown): SupportedLanguage | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const code = nullableString(record.code);
  if (!code) return null;
  return {
    code: code.toLowerCase(),
    english_label: nullableString(record.english_label) ?? code.toUpperCase(),
    endonym: nullableString(record.endonym) ?? code.toUpperCase(),
  };
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
